import React from 'react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Form, Tooltip, OverlayTrigger, ButtonGroup, Dropdown } from 'react-bootstrap'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { vendorAPI } from '../api/vendorAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI'
import { settingsAPI } from '../api/settingsAPI'
import { calculateVolumeCubicFeet, calculateNetCaseWeight, calculateGrossCaseWeight, highlightText, formatCurrency, formatDate, formatDateOnly, formatQuantity } from '../utils/commonUtils'
import PaginationComponent from './Pagination'
import StockItemInfo from './StockItemInfo'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import { createColumnToggleDropdown, createFilterDropdown, handleSearchWithDebounce, getCountryFlag } from '../utils/tableUtils'
import 'material-icons/iconfont/material-icons.css'
import ResizableTable from './ResizableTable'
import Select from 'react-select'
import '../styles/selectControl.css'
import SearchInputWithClear from './SearchInputWithClear'

function InventoryListComponent({
    title,
    showFilters = true,
    showPagination = true,
    showColumnToggle = true,
    showBulkActions = true,
    showActions = true,
    returnPath = null,
    customActionHandler = null,
    initialFilters = {},
    defaultColumnVisibility = null,
    onRowClick,
    onBulkActionSuccess,
    additionalActions,
    onSelectionChange,
    clearSelectionTrigger,
    onDataChange,
    className
}) {
    const navigate = useNavigate()
    const location = useLocation()
    
    // State variables from InventoryListScreen
    const [inventoryItems, setInventoryItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [vendorMap, setVendorMap] = useState({})
    const [warehouseMap, setWarehouseMap] = useState({})
    const [locationMap, setLocationMap] = useState({})
    const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm || '')
    const [lastDataCount, setLastDataCount] = useState(0)
    const [searchInputValue, setSearchInputValue] = useState(initialFilters.searchTerm || '')
    const [currentPage, setCurrentPage] = useState(initialFilters.page || 1)
    const [selectedVendor, setSelectedVendor] = useState(initialFilters.vendor || '')
    const [selectedCategories, setSelectedCategories] = useState(initialFilters.categories || [])
    const [selectedWarehouse, setSelectedWarehouse] = useState(initialFilters.warehouse || '')
    const [selectedLocation, setSelectedLocation] = useState(initialFilters.location || '')
    const [selectedInTransit, setSelectedInTransit] = useState(initialFilters.inTransit || '')
    const [selectedManufacturer, setSelectedManufacturer] = useState(initialFilters.manufacturer || '')
    const [pageSize, setPageSize] = useState(initialFilters.pageSize || PaginationComponent.getCurrentPageSize())
    const [totalPages, setTotalPages] = useState(0)
    const [totalQuantity, setTotalQuantity] = useState(0)
    const [totalCases, setTotalCases] = useState(0)
    const [totalWeightLbs, setTotalWeightLbs] = useState(0)
    const [totalPallets, setTotalPallets] = useState(0)
    const [totalFilm, setTotalFilm] = useState(0)
    const [categoryMap, setCategoryMap] = useState({})
    const [systemSettings, setSystemSettings] = useState({
        enable_warehouse_transit: true
    })

    // Function to navigate to a new route while preserving the filter state
    const navigateWithFilterState = (path) => {
        let finalPath = path;
        
        // If returnPath is specified (for widgets), add it as a query parameter
        if (returnPath) {
            const separator = path.includes('?') ? '&' : '?';
            finalPath = `${path}${separator}returnTo=${encodeURIComponent(returnPath)}`;
        }
        
        navigate(finalPath, {
            state: {
                returnFilters: location.search
            }
        });
    };

    // Function to handle action clicks - uses custom handler if provided
    const handleActionClick = (actionPath, row) => {
        if (customActionHandler) {
            customActionHandler(actionPath, row, returnPath);
        } else {
            navigateWithFilterState(actionPath);
        }
    };

    // Column labels for the table
    const columnLabels = {
        actions: 'Actions',
        selection: 'Select',
        inventory_id: 'Inventory ID',
        stock_item_id: 'Stock Item ID',
        sku: 'SKU',
        image: 'Image',
        gtin: 'GTIN',
        upc: 'UPC',
        description: 'Description',
        lot: 'LOT',
        notes: 'Notes',
        receivedDate: 'Received Date',
        bbDate: 'Best Before',
        warehouse: 'Warehouse',
        location: 'Location',
        categories: 'Categories',
        manufacturer: 'Manufacturer',
        countryOfOrigin: 'Country of Origin',
        vendor: 'Vendor',
        cost: 'Cost',
        uom: 'UOM',
        itemSize: 'Item Size',
        casePack: 'Case Pack',
        netCaseWeight: 'Net Case Weight',
        packageWeight: 'Package Weight',
        grossCaseWeight: 'Gross Case Weight',
        ti: 'TI',
        hi: 'HI',
        length: 'Length (inch)',
        width: 'Width (inch)',
        height: 'Height (inch)',
        volume: 'Volume',
        quantity: 'Quantity',
        ...(systemSettings.enable_warehouse_transit && { in_transit: 'In Transit' })
    }

    // Function to toggle column visibility
    const toggleColumnVisibility = (column) => {
        if (column === 'in_transit' && !systemSettings.enable_warehouse_transit) {
            return // Don't allow toggling the transit column when warehouse transit is disabled
        }
        setColumnVisibility(prevState => ({
            ...prevState,
            [column]: !prevState[column]
        }))
    }

    // Column visibility state
    const [columnVisibility, setColumnVisibility] = useState(() => {
        // If defaultColumnVisibility is provided, use it
        if (defaultColumnVisibility) {
            return defaultColumnVisibility
        }
        // Otherwise, load from localStorage or use defaults
        const savedVisibility = localStorage.getItem('inventoryItemColumnVisibility')
        return savedVisibility ? JSON.parse(savedVisibility) : {
            actions: showActions,
            selection: showBulkActions,
            inventory_id: false,
            stock_item_id: false,
            sku: true,
            image: true,
            gtin: true,
            upc: false,
            description: true,
            lot: true,
            notes: true,
            receivedDate: false,
            bbDate: true,
            warehouse: true,
            location: true,
            categories: false,
            manufacturer: false,
            countryOfOrigin: false,
            vendor: true,
            cost: true,
            uom: false,
            itemSize: false,
            casePack: false,
            netCaseWeight: false,
            packageWeight: false,
            grossCaseWeight: false,
            ti: false,
            hi: false,
            length: false,
            width: false,
            height: false,
            volume: false,
            quantity: true,
            in_transit: true
        }
    })

    // Column widths state
    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('inventoryItemColumnWidths')
        return savedWidths ? JSON.parse(savedWidths) : {
            actions: '120px',
            selection: '60px',
            inventory_id: 'auto',
            stock_item_id: 'auto',
            sku: 'auto',
            image: 'auto',
            gtin: 'auto',
            upc: 'auto',
            description: 'auto',
            lot: 'auto',
            notes: 'auto',
            receivedDate: 'auto',
            bbDate: 'auto',
            warehouse: 'auto',
            location: 'auto',
            categories: 'auto',
            manufacturer: 'auto',
            countryOfOrigin: 'auto',
            vendor: 'auto',
            cost: 'auto',
            uom: 'auto',
            itemSize: 'auto',
            casePack: 'auto',
            netCaseWeight: 'auto',
            packageWeight: 'auto',
            grossCaseWeight: 'auto',
            ti: 'auto',
            hi: 'auto',
            length: 'auto',
            width: 'auto',
            height: 'auto',
            volume: 'auto',
            quantity: 'auto',
            in_transit: 'auto'
        }
    })

    // Bulk selection state
    const [selectedRows, setSelectedRows] = useState([])
    const [selectAll, setSelectAll] = useState(false)
    const [commonWarehouse, setCommonWarehouse] = useState(null)

    // Sorting state
    const [sortColumn, setSortColumn] = useState(initialFilters.sortColumn || null)
    const [sortDirection, setSortDirection] = useState(initialFilters.sortDirection || null)

    // Save column settings to localStorage
    useEffect(() => {
        // Only save to localStorage if not using defaultColumnVisibility
        if (!defaultColumnVisibility) {
            localStorage.setItem('inventoryItemColumnVisibility', JSON.stringify(columnVisibility))
        }
    }, [columnVisibility, defaultColumnVisibility])

    useEffect(() => {
        localStorage.setItem('inventoryItemColumnWidths', JSON.stringify(columnWidths))
    }, [columnWidths])

    // Call onSelectionChange callback when selection or warehouse map changes
    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedRows, warehouseMap)
        }
    }, [selectedRows, warehouseMap, onSelectionChange])

    // Clear selection when clearSelectionTrigger changes
    useEffect(() => {
        if (clearSelectionTrigger > 0) {
            setSelectedRows([])
            setSelectAll(false)
        }
    }, [clearSelectionTrigger])

    // Handle sorting
    const handleSort = (column, direction) => {
        setSortColumn(column);
        setSortDirection(direction);
        // Data will be refetched automatically due to useEffect dependencies
    };

    // Fetch data with debounce
    const fetchTimeoutRef = useRef(null);

    useEffect(() => {
        let isMounted = true;

        async function fetchInventoryItems() {
            try {
                setLoading(true);

                // Clear any existing timeout
                if (fetchTimeoutRef.current) {
                    clearTimeout(fetchTimeoutRef.current);
                }

                // Set a new timeout to debounce the API call
                fetchTimeoutRef.current = setTimeout(async () => {
                    const response = await inventoryItemAPI.getAll(
                        currentPage,
                        pageSize,
                        searchTerm,
                        selectedVendor,
                        selectedManufacturer,
                        selectedCategories.join(','),
                        initialFilters.bbStartDate || null,
                        initialFilters.bbEndDate || null,
                        initialFilters.receivedStartDate || null,
                        initialFilters.receivedEndDate || null,
                        selectedWarehouse,
                        selectedLocation,
                        selectedInTransit,
                        sortColumn,
                        sortDirection
                    );

                    // Only update state if component is still mounted
                    if (isMounted) {
                        // Handle the paginated response structure
                        if (!response || typeof response !== 'object') {
                            throw new Error('Invalid response from API');
                        }

                        // Get the data array and totals from the results object
                        const results = response.results || {};
                        const data = results.results || [];
                        
                        if (!Array.isArray(data)) {
                            console.error('Invalid data structure:', { response, results, data });
                            throw new Error('Expected an array of inventory items');
                        }

                        // Set all the state at once to minimize re-renders
                        setInventoryItems(data);
                        setTotalPages(response.count);
                        setTotalQuantity(results.total_quantity || 0);
                        setTotalCases(results.total_cases || 0);
                        setTotalWeightLbs(results.total_weight_lbs || 0);
                        setTotalPallets(results.total_pallets || 0);
                        setTotalFilm(results.total_film || 0);

                        // Debug logging for film items
                        if (results.total_film > 0) {
                            console.log('Film items detected:', results.total_film);
                        }
                        
                        // Notify parent component of data change only if count changed
                        if (onDataChange && data.length !== lastDataCount) {
                            onDataChange(data.length);
                            setLastDataCount(data.length);
                        }
                    }
                }, 300); // 300ms debounce
            } catch (error) {
                console.error('Error fetching inventory items:', error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        fetchInventoryItems();

        // Cleanup function
        return () => {
            isMounted = false;
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [
        currentPage,
        searchTerm,
        selectedVendor,
        selectedManufacturer,
        selectedCategories,
        selectedWarehouse,
        selectedLocation,
        selectedInTransit,
        pageSize,
        initialFilters.bbStartDate,
        initialFilters.bbEndDate,
        initialFilters.receivedStartDate,
        initialFilters.receivedEndDate,
        sortColumn,
        sortDirection
    ]);

    // Fetch all reference data in a single effect
    useEffect(() => {
        let isMounted = true;

        async function fetchReferenceData() {
            try {
                // Fetch all reference data in parallel
                const [vendors, warehouses, categories, settings] = await Promise.all([
                    vendorAPI.getAll(),
                    warehouseAPI.getAll(),
                    stockItemCategoryAPI.getAll(),
                    settingsAPI.get()
                ]);

                // Only update state if component is still mounted
                if (isMounted) {
                    // Create vendor mapping
                    const vendorMapping = vendors.reduce((acc, vendor) => {
                        acc[vendor._id] = vendor.name;
                        return acc;
                    }, {});
                    setVendorMap(vendorMapping);

                    // Create warehouse mapping
                    const warehouseMapping = warehouses.reduce((acc, warehouse) => {
                        acc[warehouse._id] = warehouse.name;
                        return acc;
                    }, {});
                    setWarehouseMap(warehouseMapping);

                    // Create category mapping
                    const categoryMapping = categories.reduce((acc, category) => {
                        acc[category._id] = category.name;
                        return acc;
                    }, {});
                    setCategoryMap(categoryMapping);

                    // Set system settings
                    setSystemSettings(settings);
                }
            } catch (error) {
                console.error('Error fetching reference data:', error);
            }
        }

        fetchReferenceData();

        // Cleanup function to prevent setting state on unmounted component
        return () => {
            isMounted = false;
        };
    }, []); // Empty dependency array means this runs once on mount

    // Fetch locations only when warehouse changes
    useEffect(() => {
        let isMounted = true;

        async function fetchLocations() {
            try {
                if (!selectedWarehouse) {
                    setLocationMap({});
                    return;
                }

                const locations = await locationAPI.getAll(selectedWarehouse);
                
                if (isMounted) {
                    const mapping = locations.reduce((acc, location) => {
                        acc[location._id] = location.name;
                        return acc;
                    }, {});
                    setLocationMap(mapping);
                }
            } catch (error) {
                console.error('Error fetching locations:', error);
            }
        }

        fetchLocations();

        return () => {
            isMounted = false;
        };
    }, [selectedWarehouse]); // Only re-run when selectedWarehouse changes

    // Event handlers
    const handleVendorFilterChange = (selectedOption) => {
        setSelectedVendor(selectedOption ? selectedOption.value : '')
        setCurrentPage(1)
    }

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber)
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        setCurrentPage(1)
    }

    const handleSearchInputChange = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        handleSearchWithDebounce(value, setSearchTerm, setCurrentPage)
    }

    const handleInTransitFilterChange = (inTransit) => {
        setSelectedInTransit(inTransit)
        setCurrentPage(1)
    }

    const handleCategorySelection = (e, categoryId) => {
        if (e.target.checked) {
            setSelectedCategories(prev => [...prev, categoryId])
        } else {
            setSelectedCategories(prev => prev.filter(id => id !== categoryId))
        }
        setCurrentPage(1)
    }

    const handleWarehouseFilterChange = (selectedOption) => {
        setSelectedWarehouse(selectedOption ? selectedOption.value : '')
        setSelectedLocation('')
        setCurrentPage(1)
    }

    const handleLocationFilterChange = (selectedOption) => {
        setSelectedLocation(selectedOption ? selectedOption.value : '')
        setCurrentPage(1)
    }

    const handleManufacturerFilterChange = (selectedOption) => {
        setSelectedManufacturer(selectedOption ? selectedOption.value : '')
        setCurrentPage(1)
    }

    const handleRowSelect = (row, isSelected) => {
        if (isSelected) {
            const selectedItem = {
                id: row._id,
                quantity: row.quantity,
                warehouse_id: row.warehouse_id || row.location?.warehouse_id,
                warehouse_name: row.warehouse_name
            }
            setSelectedRows(prev => [...prev, selectedItem])
        } else {
            setSelectedRows(prev => prev.filter(item => item.id !== row._id))
        }
    }

    const handleSelectAll = (isSelected) => {
        setSelectAll(isSelected)
        if (isSelected) {
            const allSelectableRows = inventoryItems
                .filter(item => !item.transit)
                .map(item => ({
                    id: item._id,
                    quantity: item.quantity,
                    warehouse_id: item.warehouse_id,
                    warehouse_name: item.warehouse_name
                }))
            setSelectedRows(allSelectableRows)
        } else {
            setSelectedRows([])
        }
    }

    const isRowSelected = (rowId) => {
        return selectedRows.some(item => item.id === rowId)
    }

    const formatVendorOptions = () => {
        return Object.entries(vendorMap).map(([id, name]) => ({
            value: id,
            label: name
        }))
    }

    const formatWarehouseOptions = () => {
        return Object.entries(warehouseMap).map(([id, name]) => ({
            value: id,
            label: name
        }))
    }

    const formatLocationOptions = () => {
        return Object.entries(locationMap).map(([id, name]) => ({
            value: id,
            label: name
        }))
    }

    return (
        <div className={className}>
            {/* Header */}
            {(title || showColumnToggle) && (
                <Row className="mb-3 align-items-center">
                    {title && (
                        <Col>
                            <h2>{title}</h2>
                        </Col>
                    )}
                    {showColumnToggle && (
                        <Col>
                            {createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}
                        </Col>
                    )}
                </Row>
            )}

            {/* Filters */}
            {showFilters && (
                <Row className="mb-3">
                    <Col md={4}>
                        <SearchInputWithClear
                            value={searchInputValue}
                            onChange={handleSearchInputChange}
                            onClear={handleClearSearch}
                            placeholder="Search by any field"
                            autoComplete="off"
                            spellCheck="false"
                        />
                    </Col>
                    <Col md={2}>
                        <Select
                            value={selectedWarehouse ? { value: selectedWarehouse, label: warehouseMap[selectedWarehouse] } : null}
                            onChange={handleWarehouseFilterChange}
                            options={formatWarehouseOptions()}
                            isSearchable
                            isClearable
                            placeholder="Warehouse"
                        />
                    </Col>
                    <Col md={2}>
                        <Select
                            value={selectedLocation ? { value: selectedLocation, label: locationMap[selectedLocation] } : null}
                            onChange={handleLocationFilterChange}
                            options={formatLocationOptions()}
                            isSearchable
                            isClearable
                            placeholder="Location"
                            isDisabled={!selectedWarehouse}
                        />
                    </Col>
                    <Col md={2}>
                        <Select
                            value={selectedVendor ? { value: selectedVendor, label: vendorMap[selectedVendor] } : null}
                            onChange={handleVendorFilterChange}
                            options={formatVendorOptions()}
                            isSearchable
                            isClearable
                            placeholder="Vendor"
                        />
                    </Col>
                    {systemSettings.enable_warehouse_transit && (
                        <Col md={2}>
                            <Dropdown onSelect={handleInTransitFilterChange}>
                                <Dropdown.Toggle variant="light" id="transit-filter-dropdown">
                                    {(() => {
                                        switch(selectedInTransit) {
                                            case 'true': return 'In Transit'
                                            case 'false': return 'Not in Transit'
                                            default: return 'Transit'
                                        }
                                    })()}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item eventKey="">All</Dropdown.Item>
                                    <Dropdown.Item eventKey="true">In Transit</Dropdown.Item>
                                    <Dropdown.Item eventKey="false">Not In Transit</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Col>
                    )}
                </Row>
            )}

            {/* Bulk Actions */}
            {showBulkActions && selectedRows.length > 0 && !onSelectionChange && (
                <Row className="mb-3">
                    <Col>
                        <div className="d-flex align-items-center bg-body-secondary p-2 rounded">
                            <span className="me-3 text-body">
                                <strong>{selectedRows.length}</strong> item{selectedRows.length !== 1 ? 's' : ''} selected
                            </span>
                            {additionalActions && additionalActions(selectedRows, warehouseMap)}
                            <Button 
                                variant="outline-danger" 
                                size="sm" 
                                onClick={() => {
                                    setSelectedRows([])
                                    setSelectAll(false)
                                }}
                            >
                                Clear Selection
                            </Button>
                        </div>
                    </Col>
                </Row>
            )}

            {/* Table */}
            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <div className="table-responsive">
                    <ResizableTable
                        tableId="inventory_items_table"
                        enableSorting={true}
                        onSort={handleSort}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        columns={[
                            ...(showActions && columnVisibility.actions ? [{
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    <Dropdown>
                                        <Dropdown.Toggle variant="primary" size="sm">
                                            Actions
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {row.transit ? (
                                                <Dropdown.Item 
                                                    onClick={() => handleActionClick(`/transfers/${row.transit}/receive`, row)}
                                                >
                                                    <i className="material-icons">download</i> Receive Transfer
                                                </Dropdown.Item>
                                            ) : (
                                                <>
                                                    <Dropdown.Item
                                                        onClick={() => handleActionClick(`/inventory/${row._id}/amend`, row)}
                                                    >
                                                        <i className="material-icons">edit</i> Edit
                                                    </Dropdown.Item>
                                                    <Dropdown.Item 
                                                        onClick={() => handleActionClick(`/inventory/${row._id}/move`, row)}
                                                    >
                                                        <i className="material-icons">trolley</i> Move
                                                    </Dropdown.Item>
                                                    {systemSettings.enable_warehouse_transit && (
                                                        <Dropdown.Item 
                                                            onClick={() => handleActionClick(`/inventory/${row._id}/transfer`, row)}
                                                        >
                                                            <i className="material-icons">local_shipping</i> Transfer
                                                        </Dropdown.Item>
                                                    )}
                                                    <Dropdown.Item 
                                                        onClick={() => handleActionClick(`/inventory/${row._id}/dispose`, row)}
                                                    >
                                                        <i className="material-icons">delete</i> Dispose
                                                    </Dropdown.Item>
                                                    <Dropdown.Item 
                                                        onClick={() => handleActionClick(`/inventory/${row._id}/return`, row)}
                                                    >
                                                        <i className="material-icons">keyboard_return</i> Return
                                                    </Dropdown.Item>
                                                    <Dropdown.Item 
                                                        onClick={() => handleActionClick(`/inventory/${row._id}/issue`, row)}
                                                    >
                                                        <i className="material-icons">sell</i> Issue
                                                    </Dropdown.Item>
                                                </>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )
                            }] : []),
                            ...(showBulkActions && columnVisibility.selection ? [{
                                id: 'selection',
                                header: () => (
                                    <Form.Check
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        label=""
                                    />
                                ),
                                cell: (row) => (
                                    <Form.Check
                                        type="checkbox"
                                        checked={isRowSelected(row._id)}
                                        onChange={(e) => handleRowSelect(row, e.target.checked)}
                                        disabled={row.transit}
                                        label=""
                                    />
                                )
                            }] : []),
                            ...(columnVisibility.inventory_id ? [{
                                id: 'inventory_id',
                                header: 'Inventory ID',
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit && (
                                            <small className="text-info">In Transit</small>
                                        )}
                                        <span>{highlightText(row._id, searchTerm)}</span>
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.stock_item_id ? [{
                                id: 'stock_item_id',
                                header: 'Stock Item ID',
                                cell: (row) => highlightText(row.stock_item._id, searchTerm)
                            }] : []),
                            ...(columnVisibility.sku ? [{
                                id: 'sku',
                                header: 'SKU',
                                sortable: true,
                                cell: (row) => highlightText(row.stock_item.sku, searchTerm)
                            }] : []),
                            ...(columnVisibility.image ? [{
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.stock_item.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo stockItem={row.stock_item} />
                                            </Tooltip>
                                        }
                                    >
                                        <img 
                                            src={row.stock_item.image} 
                                            alt="Stock Item Thumbnail"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            }] : []),
                            ...(columnVisibility.gtin ? [{
                                id: 'gtin',
                                header: 'GTIN',
                                cell: (row) => highlightText(row.stock_item.gtin, searchTerm)
                            }] : []),
                            ...(columnVisibility.upc ? [{
                                id: 'upc',
                                header: 'UPC',
                                cell: (row) => highlightText(row.stock_item.upc, searchTerm)
                            }] : []),
                            ...(columnVisibility.description ? [{
                                id: 'description',
                                header: 'Description',
                                sortable: true,
                                cell: (row) => highlightText(row.stock_item.description, searchTerm)
                            }] : []),
                            ...(columnVisibility.lot ? [{
                                id: 'lot',
                                header: 'LOT',
                                sortable: true,
                                cell: (row) => highlightText(row.lot, searchTerm)
                            }] : []),
                            ...(columnVisibility.notes ? [{
                                id: 'notes',
                                header: 'Notes',
                                cell: (row) => highlightText(row.notes, searchTerm)
                            }] : []),
                            ...(columnVisibility.receivedDate ? [{
                                id: 'receivedDate',
                                header: 'Received Date',
                                cell: (row) => formatDate(row.received_datetime)
                            }] : []),
                            ...(columnVisibility.bbDate ? [{
                                id: 'bbDate',
                                header: 'Best Before',
                                sortable: true,
                                cell: (row) => formatDateOnly(row.bb_date)
                            }] : []),
                            ...(columnVisibility.warehouse ? [{
                                id: 'warehouse',
                                header: 'Warehouse',
                                sortable: true,
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit ? (
                                            <span className="text-info">In Transit</span>
                                        ) : (
                                            <span>{row.warehouse_name}</span>
                                        )}
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.location ? [{
                                id: 'location',
                                header: 'Location',
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit ? (
                                            <span className="text-info">In Transit</span>
                                        ) : (
                                            <span>{row.location_name}</span>
                                        )}
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.categories ? [{
                                id: 'categories',
                                header: 'Categories',
                                cell: (row) => row.stock_item.categories?.map(categoryId => categoryMap[categoryId]).join(', ')
                            }] : []),
                            ...(columnVisibility.manufacturer ? [{
                                id: 'manufacturer',
                                header: 'Manufacturer',
                                cell: (row) => vendorMap[row.stock_item.manufacturer] || row.stock_item.manufacturer || 'N/A'
                            }] : []),
                            ...(columnVisibility.countryOfOrigin ? [{
                                id: 'countryOfOrigin',
                                header: 'Country of Origin',
                                cell: (row) => row.stock_item.country_of_origin ? (
                                    <div className="d-flex align-items-center">
                                        {getCountryFlag(row.stock_item.country_of_origin)}
                                        <span className="ms-1">{row.stock_item.country_of_origin}</span>
                                    </div>
                                ) : null
                            }] : []),
                            ...(columnVisibility.vendor ? [{
                                id: 'vendor',
                                header: 'Vendor',
                                cell: (row) => vendorMap[row.vendor] || row.vendor || 'N/A'
                            }] : []),
                            ...(columnVisibility.cost ? [{
                                id: 'cost',
                                header: 'Cost',
                                sortable: true,
                                cell: (row) => row.cost ? formatCurrency(row.cost) : ''
                            }] : []),
                            ...(columnVisibility.uom ? [{
                                id: 'uom',
                                header: 'UOM',
                                cell: (row) => {
                                    const uomMap = {
                                        'bag': 'Bag',
                                        'bulk': 'Bulk',
                                        'ct': 'Count',
                                        'film': 'Film',
                                        'jar': 'Jar',
                                        'pallet': 'Pallet',
                                        'tote_bag': 'Tote Bag'
                                    };
                                    return uomMap[row.stock_item.uom] || row.stock_item.uom || '';
                                }
                            }] : []),
                            ...(columnVisibility.itemSize ? [{
                                id: 'itemSize',
                                header: 'Item Size',
                                cell: (row) => row.stock_item.item_size ? `${row.stock_item.item_size} ${row.stock_item.item_size_units}` : ''
                            }] : []),
                            ...(columnVisibility.casePack ? [{
                                id: 'casePack',
                                header: 'Case Pack',
                                cell: (row) => row.stock_item.case_pack || ''
                            }] : []),
                            ...(columnVisibility.netCaseWeight ? [{
                                id: 'netCaseWeight',
                                header: 'Net Case Weight',
                                cell: (row) => calculateNetCaseWeight(row.stock_item)
                            }] : []),
                            ...(columnVisibility.packageWeight ? [{
                                id: 'packageWeight',
                                header: 'Package Weight',
                                cell: (row) => row.stock_item.package_weight ? `${row.stock_item.package_weight} ${row.stock_item.package_weight_units}` : ''
                            }] : []),
                            ...(columnVisibility.grossCaseWeight ? [{
                                id: 'grossCaseWeight',
                                header: 'Gross Case Weight',
                                cell: (row) => calculateGrossCaseWeight(row.stock_item)
                            }] : []),
                            ...(columnVisibility.ti ? [{
                                id: 'ti',
                                header: 'TI',
                                cell: (row) => row.stock_item.ti || ''
                            }] : []),
                            ...(columnVisibility.hi ? [{
                                id: 'hi',
                                header: 'HI',
                                cell: (row) => row.stock_item.hi || ''
                            }] : []),
                            ...(columnVisibility.length ? [{
                                id: 'length',
                                header: 'Length (inch)',
                                cell: (row) => row.stock_item.dimensions_l || ''
                            }] : []),
                            ...(columnVisibility.width ? [{
                                id: 'width',
                                header: 'Width (inch)',
                                cell: (row) => row.stock_item.dimensions_w || ''
                            }] : []),
                            ...(columnVisibility.height ? [{
                                id: 'height',
                                header: 'Height (inch)',
                                cell: (row) => row.stock_item.dimensions_h || ''
                            }] : []),
                            ...(columnVisibility.volume ? [{
                                id: 'volume',
                                header: 'Volume',
                                cell: (row) => calculateVolumeCubicFeet(row.stock_item)
                            }] : []),
                            ...(columnVisibility.quantity ? [{
                                id: 'quantity',
                                header: () => (
                                    <>
                                        Quantity
                                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                                            {totalQuantity > 0 && (
                                                <div
                                                    className="badge bg-primary"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Items: {totalQuantity.toLocaleString()}
                                                </div>
                                            )}
                                            {totalCases > 0 && (
                                                <div
                                                    className="badge bg-info"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Cases: {totalCases.toLocaleString()}
                                                </div>
                                            )}
                                            {totalWeightLbs > 0 && (
                                                <div
                                                    className="badge bg-warning"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Weight: {totalWeightLbs.toLocaleString()} lbs
                                                </div>
                                            )}
                                            {totalPallets > 0 && (
                                                <div
                                                    className="badge bg-secondary"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Pallets: {totalPallets.toLocaleString()}
                                                </div>
                                            )}
                                            {totalFilm > 0 && (
                                                <div
                                                    className="badge bg-secondary"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Film: {totalFilm.toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ),
                                sortable: true,
                                cell: (row) => formatQuantity(row.quantity) || 0
                            }] : []),
                            ...(columnVisibility.in_transit && systemSettings.enable_warehouse_transit ? [{
                                id: 'in_transit',
                                header: 'In Transit',
                                cell: (row) => row.transit ? 'Yes' : 'No'
                            }] : [])
                        ]}
                        data={inventoryItems}
                        initialColumnWidths={columnWidths}
                        striped
                        hover
                        onRowClick={onRowClick}
                    />

                    {/* Pagination */}
                    {showPagination && (
                        <PaginationComponent
                            currentPage={currentPage}
                            totalItems={totalPages}
                            onPageChange={handlePageChange}
                            onItemsPerPageChange={setPageSize}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default InventoryListComponent
